import { Suspense } from 'react';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import './App.css';
import Layout from './Layout';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { authLink } from '@/lib/supabase';
import ErrorBoundary from '@/components/ErrorBoundary';
import InstallPrompt from '@/components/InstallPrompt';
import NotFound from '@/pages/NotFound';
import SetPassword from '@/pages/SetPassword';
import SignIn from '@/pages/SignIn';

import Home from '@/pages/Home';
import ProcessedMailings from '@/pages/ProcessedMailings';
import Review from '@/pages/Review';
import Settings from '@/pages/Settings';
import Shipments from '@/pages/Shipments';

const PAGES = [
  { path: '/', element: <Home /> },
  { path: '/home', element: <Home /> },
  { path: '/processedmailings', element: <ProcessedMailings /> },
  { path: '/review', element: <Review /> },
  { path: '/shipments', element: <Shipments /> },
  { path: '/settings', element: <Settings />, adminOnly: true },
];

/**
 * Keep operators out of an admin screen.
 *
 * The sidebar already hides the link, but a hidden link is not a control - the
 * URL still works if someone types it or has it bookmarked from when they were
 * an admin. The API refuses the writes either way; this is so the refusal
 * arrives as an explanation rather than as a screen full of failing saves.
 */
function AdminOnly({ children }) {
  const { isAdmin } = useAuth();

  if (!isAdmin) {
    return (
      <div className="page-container">
        <div className="card" style={{ maxWidth: '32rem', margin: '3rem auto', padding: '2rem' }}>
          <h2 style={{ marginTop: 0 }}>Administrators only</h2>
          <p className="muted">
            Settings holds the shop's permit details, templates and integration
            credentials. Ask an administrator if you need something changed here.
          </p>
        </div>
      </div>
    );
  }

  return children;
}

function Spinner() {
  return (
    <div className="page-spinner">
      <Loader2 size={32} className="spin" />
    </div>
  );
}

function Shell() {
  const { loading, isAuthenticated, session, error } = useAuth();

  // An invite or reset link lands here with a live session but no usable
  // password. Handle it before the normal signed-in path, or the person gets
  // into the app once and is locked out on their next visit.
  if (authLink?.error) {
    return <SetPassword linkError={authLink.error} />;
  }
  if (authLink?.type === 'invite' || authLink?.type === 'recovery') {
    if (loading) return <Spinner />;
    return (
      <SetPassword
        mode={authLink.type === 'recovery' ? 'recovery' : 'invite'}
        email={session?.user?.email ?? ''}
      />
    );
  }

  if (loading) return <Spinner />;

  // Signed in with Supabase but no profile: the account exists and has not been
  // added to the shop. Saying so beats bouncing them back to a login that works.
  if (session && !isAuthenticated) {
    return (
      <div className="signin-shell">
        <div className="signin-card">
          <h1>Account not set up</h1>
          <p>{error ?? 'Ask an administrator to add you to the EDDM Organizer.'}</p>
          <SignOutButton />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <SignIn />;

  return (
    <Suspense fallback={<Spinner />}>
      <InstallPrompt />
      <Routes>
        <Route
          element={
            <Layout>
              <Outlet />
            </Layout>
          }
        >
          {PAGES.map((page) => (
            <Route
              key={page.path}
              path={page.path}
              element={
                <ErrorBoundary key={page.path}>
                  {page.adminOnly ? <AdminOnly>{page.element}</AdminOnly> : page.element}
                </ErrorBoundary>
              }
            />
          ))}
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function SignOutButton() {
  const { signOut } = useAuth();
  return (
    <button className="btn btn-outline" onClick={signOut}>
      Sign out
    </button>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </BrowserRouter>
  );
}
